import axios, { AxiosRequestConfig } from 'axios';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import { useConfigStore } from 'Hooks/zustand/useConfigStore';
import { RULESET_ID_SESSION_KEY } from 'Utils';
import { AuthService } from './authService';

/**
 * TODO: Create new Axios instance that include the Rule Set ID Params
 *
 * @example
 * const instance = axios.create({
 *   baseURL: 'https://some-domain.com/api/',
 * });
 * instance.interceptors.request.use(async (config) => {
 *   const ruleSetId = sessionStorage.getItem(RULESET_ID_SESSION_KEY);
 *   config.params.ruleSetId = ruleSetId;
 *   return config;
 * });
 * instance.interceptors.response.use();
 * // And now you are able to use the modified Axios headers for endpoints that needs it
 * instance.get();
 */
const shouldApplyRuleSetIdInterceptor = (url: AxiosRequestConfig['url'], ruleSetId: string | null): boolean => {
    const config = useConfigStore.getState().appConfig;

    if (!url || !ruleSetId || !config) return false;

    const whitelistPaths = [
        config.rulesUrl,
        config.repositoryUrl,
        config.reportsURL,
        config.simulationsURL,
        config.parametersUrl,
        config.uploaderURL
    ];

    const blacklistPaths = [`${config.rulesUrl}/ruleSets/`];

    const inWhitelist = whitelistPaths.some((baseUrl) => url.includes(baseUrl));
    const inBlacklist = blacklistPaths.some((baseUrl) => url.includes(baseUrl));
    return inWhitelist && !inBlacklist;
};

/**
 * On request add the bearer token from the Session storage
 */
axios.interceptors.request.use(
    async (config) => {
        const token = await AuthService?.getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        }
        const ruleSetId = sessionStorage.getItem(RULESET_ID_SESSION_KEY);
        if (shouldApplyRuleSetIdInterceptor(config.url, ruleSetId)) {
            if (!config.params) {
                config.params = {};
            }
            config.params.ruleSetId = ruleSetId;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error.response);
    }
);

/**
 * On an error response refresh the access token and
 * use the new access token from Session storage
 */
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error?.config;
        /** If the access - and refresh token is invalid clear storage and redirect to `/`  */
        if (
            error?.response?.data?.error === 'invalid_grant' &&
            error?.response?.status === 400 &&
            !originalRequest._retry
        ) {
            sessionStorage.clear();
            localStorage.clear();
            window.location.replace('/');
        }
        /** If access token is invalid, get another token with the refresh token */
        if (
            (error?.response?.status === 403 && !originalRequest._retry) ||
            (error?.response?.status === 401 && !originalRequest._retry)
        ) {
            originalRequest._retry = true;
            await AuthService?.refreshAccessToken();
            const token = await AuthService?.getAccessToken();
            useAccessTokenStore.setState({ accessToken: token });
            if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            }
            return axios(originalRequest);
        }
        return Promise.reject(error);
    }
);
